import React from 'react'
import { Label, Textarea, Checkbox, Button } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Shuffle, Dice6 } from 'lucide-react'
import type { WorldBuilderInput } from '@creanizer/types'

interface RandomModeFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
}

export function RandomModeForm({ input, onChange }: RandomModeFormProps) {
  const generateRandomParameters = () => {
    // ランダムにパラメータを生成
    const randomParams = {
      magicLevel: Math.floor(Math.random() * 10) + 1,
      scienceLevel: Math.floor(Math.random() * 10) + 1,
      civilizationLevel: Math.floor(Math.random() * 10) + 1,
      dangerLevel: Math.floor(Math.random() * 10) + 1,
      mysteryLevel: Math.floor(Math.random() * 10) + 1,
      complexityLevel: Math.floor(Math.random() * 10) + 1,
      uniquenessLevel: Math.floor(Math.random() * 10) + 1,
      chaosLevel: Math.floor(Math.random() * 10) + 1,
    }
    
    onChange({
      ...input,
      parameters: randomParams,
    })
  }
  
  const handleMustIncludeChange = (value: string) => {
    onChange({
      ...input,
      randomConstraints: {
        ...input.randomConstraints,
        mustInclude: value.split('\n').filter(Boolean),
      },
    })
  }
  
  const handleMustExcludeChange = (value: string) => {
    onChange({
      ...input,
      randomConstraints: {
        ...input.randomConstraints,
        mustExclude: value.split('\n').filter(Boolean),
      },
    })
  }
  
  const handleInspirationSourcesChange = (value: string) => {
    onChange({
      ...input,
      randomConstraints: {
        ...input.randomConstraints,
        inspirationSources: value.split('\n').filter(Boolean),
      },
    })
  }
  
  // コンポーネントマウント時にランダムパラメータを生成
  React.useEffect(() => {
    if (input.mode === 'random' && !input.parameters) {
      generateRandomParameters()
    }
  }, [input.mode])
  
  return (
    <div className="space-y-4">
      <Alert>
        <Shuffle className="h-4 w-4" />
        <AlertDescription>
          ランダムモードでは、完全に独創的で予測不可能な世界観を生成します。
          世界観パラメータは自動でランダムに設定されます。
        </AlertDescription>
      </Alert>
      
      <div className="p-3 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium">現在のランダムパラメータ</p>
          <Button
            size="sm"
            variant="outline"
            onClick={generateRandomParameters}
          >
            <Dice6 className="h-4 w-4 mr-1" />
            再生成
          </Button>
        </div>
        {input.parameters && (
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div>魔法: {input.parameters.magicLevel}/10</div>
            <div>科学: {input.parameters.scienceLevel}/10</div>
            <div>文明: {input.parameters.civilizationLevel}/10</div>
            <div>危険: {input.parameters.dangerLevel}/10</div>
            <div>神秘: {input.parameters.mysteryLevel}/10</div>
            <div>複雑: {input.parameters.complexityLevel}/10</div>
            <div>独自: {input.parameters.uniquenessLevel}/10</div>
            <div>混沌: {input.parameters.chaosLevel}/10</div>
          </div>
        )}
      </div>
      
      <div>
        <Label htmlFor="mustInclude">必ず含める要素（各行に1つずつ）</Label>
        <Textarea
          id="mustInclude"
          value={input.randomConstraints?.mustInclude?.join('\n') || ''}
          onChange={(e) => handleMustIncludeChange(e.target.value)}
          placeholder="例:&#10;魔法&#10;古代文明&#10;空飛ぶ城"
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          これらの要素は必ず世界観に含まれます
        </p>
      </div>
      
      <div>
        <Label htmlFor="mustExclude">絶対に含めない要素（各行に1つずつ）</Label>
        <Textarea
          id="mustExclude"
          value={input.randomConstraints?.mustExclude?.join('\n') || ''}
          onChange={(e) => handleMustExcludeChange(e.target.value)}
          placeholder="例:&#10;現代技術&#10;宇宙&#10;ゾンビ"
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          これらの要素は世界観から除外されます
        </p>
      </div>
      
      <div>
        <Label htmlFor="inspirationSources">インスピレーション源（各行に1つずつ）</Label>
        <Textarea
          id="inspirationSources"
          value={input.randomConstraints?.inspirationSources?.join('\n') || ''}
          onChange={(e) => handleInspirationSourcesChange(e.target.value)}
          placeholder="例:&#10;指輪物語&#10;スタジオジブリ&#10;サイバーパンク2077"
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          これらの作品や概念から着想を得ます
        </p>
      </div>
      
      <label className="flex items-center space-x-2 cursor-pointer">
        <Checkbox
          id="unexpectedTwist"
          checked={input.randomConstraints?.unexpectedTwist ?? false}
          onCheckedChange={(checked) => onChange({
            ...input,
            randomConstraints: {
              ...input.randomConstraints,
              unexpectedTwist: !!checked,
            },
          })}
        />
        <div className="space-y-1">
          <span className="text-sm font-medium">予想外の展開を含む</span>
          <p className="text-xs text-muted-foreground">
            一般的な世界観の常識を覆す要素を追加
          </p>
        </div>
      </label>
      
      <div className="p-3 bg-amber-50 rounded-lg">
        <p className="text-sm text-amber-700">
          ⚠️ ランダムモードは非常に創造的な結果を生成しますが、
          一貫性や論理性が低下する可能性があります。
        </p>
      </div>
    </div>
  )
}